import { useNavigation } from '@react-navigation/native';
import { Button } from 'aimo-ui';
import ParkingSignInButton from 'components/park/ParkingSignInButton';
import {
  FooterButtonContainer,
  VerticalDistributedContainer,
} from 'components/sheets/SheetLayout';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useResumableParkingSession from 'hooks/useResumableParkingSession';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import { SelectedPaymentMethod } from 'hooks/useSelectedPaymentMethod';
import { isEmpty } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkingStore } from 'stores/parkingStore';
import { Vehicle } from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import {
  getMissingPaymentTextKey,
  getMissingVehicleTextKey,
} from 'utils/parkingButtonValidation';
import { arePaymentMethodsEqual } from 'utils/paymentUtils';
import PoolingFullInfo from '../pooling/PoolingFullInfo';
import DisplayStartButton from './DisplayStartAnprButton';
import PaymentCardDropDown from './PaymentCardDropDown';
import VehicleSelectDropDown from './VehicleSelectDropDown';
import { useUiStore } from 'stores/uiStore';

const EnableAnprButton = () => {
  const { navigate } = useNavigation<RootNavProps>();
  const { t } = useTranslation();

  const { isLoggedIn, loginLoading } = useIsLoggedIn();

  const { parkingZoneResult } = useContext(ParkingContext) || {};

  const {
    customer: { vehicles },
  } = useContext(AccountContext);

  const {
    parkingConditions,
    selectedParkingVehiclePlate,
    setParkingConditions,
    setParkingVehiclePlate,
  } = useParkingStore((state) => state);
  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const { cameraRecognition } = selectedVehicle || {};
  const anprText = cameraRecognition?.enabled
    ? 'parking.startsAutomatically'
    : 'parking.allowAnpr';
  const { paymentMethod, selectedParkingZoneId } = parkingConditions ?? {};
  useResumableParkingSession();
  const { companyPays } = useGetPoolingInfo(selectedParkingZoneId);
  const { addCard, loadingAddCard } = useAddPaymentMethod();

  const hasVehicles = !!(vehicles && vehicles.length > 0);

  const noPaymentMethodAndCompanyWontPay =
    isEmpty(paymentMethod) && !companyPays;

  const manualStart =
    parkingZoneResult?.data?.parkZone?.parkingMethods?.manualStart;

  const displayConditions =
    manualStart && selectedVehicle?.licensePlate?.plateText;

  const handleOpenAnprModal = () => {
    navigate('vehicleCameraRecognitionParking', { saveOnModalConfirm: true });
  };

  const setVehicleState = useCallback(
    (v?: Vehicle) => {
      // Only update if the vehicle actually changed
      if (v?.licensePlate?.plateText !== selectedParkingVehiclePlate) {
        setParkingVehiclePlate(v?.licensePlate?.plateText);
      }
    },
    [selectedParkingVehiclePlate, setParkingVehiclePlate]
  );

  const setCardState = useCallback(
    (selectedPaymentMethod?: SelectedPaymentMethod) => {
      if (!arePaymentMethodsEqual(selectedPaymentMethod, paymentMethod)) {
        setParkingConditions({ paymentMethod: selectedPaymentMethod });
      }
    },
    [setParkingConditions, paymentMethod]
  );

  if (!isLoggedIn) {
    return (
      <ParkingSignInButton
        testID="park-zone-details-anpr-login"
        loading={loginLoading}
      />
    );
  }

  const onPress = () => {
    if (noPaymentMethodAndCompanyWontPay && !hasVehicles) {
      // Redirect to onboarding flow
      navigate('onboardingModal', {
        screen: 'welcome',
      });
    } else if (noPaymentMethodAndCompanyWontPay) {
      // Redirect to add payment method screen
      addCard();
    } else if (!selectedVehicle && !hasVehicles) {
      // Redirect to add vehicle screen
      navigate('addVehicleParking');
    } else if (!selectedVehicle && hasVehicles) {
      useUiStore.getState().setSheetModal('parkingVehicle');
    } else {
      handleOpenAnprModal();
    }
  };

  const buttonText = () => {
    // Check for missing payment (only noPaymentMethodAndCompanyWontPay)
    const paymentTextKey = getMissingPaymentTextKey(
      false, // ANPR doesn't use hasOtherPaymentMethods
      noPaymentMethodAndCompanyWontPay
    );
    if (paymentTextKey) {
      return t(paymentTextKey);
    }

    // Check for missing vehicle
    const vehicleTextKey = getMissingVehicleTextKey(
      selectedVehicle,
      hasVehicles
    );
    if (vehicleTextKey) {
      return t(vehicleTextKey);
    }
    return t(anprText);
  };

  return (
    <FooterButtonContainer pb={0}>
      <VerticalDistributedContainer>
        <PoolingFullInfo parkingZoneUid={selectedParkingZoneId} />
        {displayConditions && (
          <DisplayStartButton parkingZoneUid={selectedParkingZoneId} />
        )}
        <FooterButtonContainer pt={0} prPl={10}>
          <VehicleSelectDropDown
            currentVehiclePlate={selectedParkingVehiclePlate}
            setVehicleState={setVehicleState}
          />
          <PaymentCardDropDown
            isAnpr
            parkingZoneUid={selectedParkingZoneId}
            setCardState={setCardState}
          />
        </FooterButtonContainer>
        <FooterButtonContainer>
          <Button
            size="l"
            text={buttonText()}
            testID="park-zone-details-allow-anpr"
            onPress={onPress}
            disabled={
              !!cameraRecognition?.enabled &&
              !!selectedVehicle &&
              !noPaymentMethodAndCompanyWontPay
            }
            loading={loadingAddCard}
          />
        </FooterButtonContainer>
      </VerticalDistributedContainer>
    </FooterButtonContainer>
  );
};

export default EnableAnprButton;
